import Div from '@/shared/Div';
import PlanCardHeader from './PlanCardHeader';
import Map from '../Map';
import ImageWithFallback from '@/globalComponents/image';
import Divider from '@/shared/Divider';
import { Fragment, useMemo } from 'react';
import Paragraph from '@/shared/Paragraph';
import classNames from 'classnames';
import { PLAN_BACKGROUND_MAP, PLAN_BOX_SHADOW } from '@/constants/amc';
import is from '@/utils/is';

const boxShadow = PLAN_BOX_SHADOW;
type Discount = {
  color: string;
  background: string;
};

type Price = {
  discount: any;
  discountedPrice: number;
  originalPrice: number;
};

type PlanCardPropsc = {
  title: string;
  tier: string;
  discount: Discount;
  isActive: boolean;
  selectPlan: (i: number) => void;
  index: number;
  deviceType: 'UV' | 'RO';
  deviceTypeMetaData: any;
  expanded: boolean;
  price: Price;
  recommended?: boolean;
  daysToExpire?: number;
  discounts?: any[];
  discountLabels?: any;
  cohortStatus?: string;
  bottomText?: string;
  id?: string;
  setABTestVariantB?: any;
  isABTestVariantB?: boolean | undefined;
  showDefaultCards?: boolean;
};

export const getDiscountTagText = (
  discountLabels: any,
  discountPercentage: any,
  daysToExpire: number,
  cohortStatus: any,
) => {
  if (is.not.empty.array(discountLabels?.[cohortStatus])) {
    let discountLabel = null;
    if (discountPercentage > 0) {
      const absoluteDaysToExpire = Math.abs(daysToExpire);
      for (const discount of discountLabels?.[cohortStatus]) {
        if (Math.abs(Number(discount.days)) >= absoluteDaysToExpire) {
          discountLabel = discount.text.replace('{{value}}', discountPercentage);
          break;
        }
      }
      return discountLabel;
    }
    return null;
  }
  return null;
};

const PlanCard = (props: PlanCardPropsc) => {
  const {
    title,
    tier,
    discount,
    isActive,
    selectPlan,
    index,
    deviceType,
    deviceTypeMetaData,
    expanded,
    price,
    recommended,
    daysToExpire,
    discountLabels,
    cohortStatus,
    bottomText,
    id,

    isABTestVariantB,
  } = props;

  const selectPlanHandler = () => {
    selectPlan(index);
  };

  const { totalBenefits = '', benefits = [] } = deviceTypeMetaData?.[deviceType] ?? {};
  const discountTagText = useMemo(
    () =>
      getDiscountTagText(
        discountLabels ?? {},
        price?.discount?.value ?? 0,
        daysToExpire ?? 0,
        cohortStatus,
      ),
    [discountLabels, price, daysToExpire, cohortStatus],
  );
  return (
    <Div
      onClick={selectPlanHandler}
      className={classNames('p-px rounded-cm cursor-pointer', {
        'bg-neutral-50': !isActive,
        hidden: isABTestVariantB,
      })}
      style={
        isActive
          ? {
              boxShadow,
              background: PLAN_BACKGROUND_MAP[tier],
            }
          : {}
      }
      id={id}
    >
      <Div
        className={classNames('rounded-cm w-full h-full sm:w-[360px] relative  bg-white', {
          'pb-4': isActive || expanded,
        })}
      >
        <PlanCardHeader
          title={title}
          subTitle={totalBenefits ?? null}
          tier={tier}
          isActive={isActive}
          expanded={expanded}
          discount={Boolean((discount && discountTagText) || recommended)}
          price={price?.originalPrice}
          discountedPrice={price?.discountedPrice}
        />
        <Map
          wrapper="ul"
          className="px-4 pt-3 font-primary"
          list={expanded ? benefits : []}
          render={({ icon, description, id }, index) => (
            <Fragment key={id + index}>
              <li className="flex gap-s3 items-center" key={id}>
                <Div className="h-6 w-6 flex items-center justify-center ">
                  <ImageWithFallback src={icon} width={16} height={16} alt="" className="mx-auto" />
                </Div>
                <Div
                  className="text-black/70 text-body-sm lg:text-body-base"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </li>
              <Divider className="ml-8 my-2 last:hidden border-neutral-50" />
            </Fragment>
          )}
        />
        {discount && discountTagText ? (
          <Paragraph
            className="absolute right-0 top-0 rounded-bl-lg rounded-tr-2xl py-1 px-3 "
            size="xs"
            style={discount}
          >
            {discountTagText}
          </Paragraph>
        ) : null}
        {recommended ? (
          <Paragraph
            className="absolute left-0 top-0 rounded-br-lg rounded-tl-2xl py-1 px-3 bg-amc-gold text-white"
            size="xs"
          >
            MOST PURCHASED
          </Paragraph>
        ) : null}
        {bottomText && isActive ? (
          <Paragraph className="text-body-sm text-extended-silver underline text-center mt-s5">
            {bottomText}
          </Paragraph>
        ) : null}
      </Div>
    </Div>
  );
};

export default PlanCard;
